import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, ShoppingCart, Wrench, FileText, RefreshCcw,
  MessageSquare, Warehouse, LogOut, X, Truck, Contact, TrendingUp, ShieldCheck,
  UserCircle, Users, ShieldQuestion, Clock,
} from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { useAuth, hasPerm, moduleVisible } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { useUnreadMessages } from "../../context/UnreadMessagesContext";

const linkClass = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
    isActive ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-accent hover:text-foreground"
  }`;

export function Sidebar({ open, onClose }) {
  const { user, modules, logout } = useAuth();
  const { count: unreadCount } = useUnreadMessages();
  const navigate = useNavigate();
  const [supportUnread, setSupportUnread] = useState(0);
  const refreshSupport = useCallback(() => {
    api.get("/support/unread-count").then((r) => setSupportUnread(r.data.count)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!user) return undefined;
    refreshSupport();
    const t = setInterval(refreshSupport, 60000);
    return () => clearInterval(t);
  }, [user, refreshSupport]);
  if (!user) return null;

  // "Tableau de bord", "Caisse", "Stock" et "Administrateurs" restent
  // toujours visibles (voir constants.ALWAYS_VISIBLE_MODULES côté backend).
  // Les autres onglets dépendent À LA FOIS des permissions de la personne
  // (hasPerm) ET de l'interrupteur global piloté depuis le panel
  // (moduleVisible) : si le panel désactive une fonction pour ce site, elle
  // disparaît pour tout le monde, même pour un administrateur.
  const items = [
    { to: "/", label: "Tableau de bord", icon: LayoutDashboard, show: true },
    { to: "/caisse", label: "Caisse", icon: ShoppingCart, show: hasPerm(user, "caisse", "access") },
    { to: "/stock", label: "Stock", icon: Package, show: hasPerm(user, "stock", "view") },
    { to: "/statistiques", label: "Statistiques", icon: TrendingUp, show: moduleVisible(modules, "statistiques") && hasPerm(user, "statistiques", "view") },
    { to: "/clients", label: "Clients", icon: Contact, show: moduleVisible(modules, "client") && hasPerm(user, "client", "view") },
    { to: "/devis", label: "Devis", icon: FileText, show: moduleVisible(modules, "devis") && hasPerm(user, "devis", "view") },
    { to: "/interventions", label: "Interventions", icon: Wrench, show: moduleVisible(modules, "intervention") && hasPerm(user, "intervention", "view") },
    { to: "/reprises", label: "Reprises", icon: RefreshCcw, show: moduleVisible(modules, "reprise") && hasPerm(user, "reprise", "view") },
    { to: "/sav", label: "SAV", icon: ShieldQuestion, show: moduleVisible(modules, "sav") && hasPerm(user, "sav", "view") },
    { to: "/commandes", label: "Commande", icon: Truck, show: moduleVisible(modules, "commande") && (hasPerm(user, "commande", "view") || hasPerm(user, "depot")) },
    { to: "/communication", label: "Messagerie", icon: MessageSquare, show: moduleVisible(modules, "communication") && hasPerm(user, "communication"), badge: unreadCount },
    { to: "/depot", label: "Dépôt", icon: Warehouse, show: moduleVisible(modules, "depot") && hasPerm(user, "depot") },
    { to: "/mon-pointage", label: "Mon pointage", icon: Clock, show: true },
    { to: "/profil", label: "Profil et demande", icon: UserCircle, show: true },
    { to: "/personnel", label: "Gestion du personnel", icon: Users, show: moduleVisible(modules, "personnel") && hasPerm(user, "personnel", "access") },
    { to: "/admin", label: "Administrateurs", icon: ShieldCheck, show: user.is_admin },
  ];

  return (
    <>
      {open && (
        <div
          onClick={onClose}
          data-testid="sidebar-overlay"
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
        />
      )}
      <aside
        data-testid="sidebar"
        className={`fixed lg:sticky top-0 left-0 z-50 w-72 lg:w-64 shrink-0 border-r border-border bg-card flex flex-col h-screen transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <div className="px-5 py-6 flex items-center justify-between">
          <div>
            <p className="font-heading text-xl font-bold tracking-tight">SOGO Gestion</p>
            <p className="text-xs text-muted-foreground mt-0.5">Boutique &amp; Dépôt</p>
          </div>
          <button onClick={onClose} data-testid="sidebar-close-button" className="lg:hidden p-1.5 rounded-md hover:bg-accent">
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {items.filter((i) => i.show).map((item) => (
            <NavLink key={item.to} to={item.to} onClick={onClose} className={linkClass} data-testid={`sidebar-link-${item.to.replace(/\//g, "-") || "home"}`}>
              <span className="relative shrink-0">
                <item.icon className="w-4.5 h-4.5" size={18} />
                {!!item.badge && (
                  <span
                    className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] leading-4 font-semibold text-center"
                    data-testid="sidebar-messagerie-unread-badge"
                  >
                    {item.badge > 99 ? "99+" : item.badge}
                  </span>
                )}
              </span>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="px-2 py-2 mb-1">
            <p className="text-sm font-medium" data-testid="sidebar-user-name">{user.prenom} {user.nom}</p>
            <p className="text-xs text-muted-foreground">{(user.grades || []).join(", ")}</p>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-destructive hover:bg-destructive/10 transition-colors duration-200"
          >
            <LogOut size={16} /> Déconnexion
          </button>
          <button
            type="button"
            onClick={() => { navigate("/assistance"); onClose?.(); }}
            data-testid="support-link"
            className="w-full mt-1 px-3 py-1 text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline text-left"
          >
            Besoin d'aide{supportUnread > 0 && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-red-500 align-middle" />}
          </button>
        </div>
      </aside>
    </>
  );
}
